import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import './ToastProvider.css';

const ToastContext = createContext(null);

const DEFAULT_DURATION = 4200;
let toastCounter = 0;

const TOAST_META = {
  success: { icon: 'OK', label: 'Success' },
  error: { icon: '!', label: 'Error' },
  warning: { icon: '!', label: 'Warning' },
  info: { icon: 'i', label: 'Info' },
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const timeoutMapRef = useRef(new Map());

  const dismissToast = useCallback((toastId) => {
    const timeoutId = timeoutMapRef.current.get(toastId);

    if (timeoutId) {
      window.clearTimeout(timeoutId);
      timeoutMapRef.current.delete(toastId);
    }

    setToasts((currentToasts) => currentToasts.filter((toast) => toast.id !== toastId));
  }, []);

  const showToast = useCallback((options) => {
    const {
      title,
      message = '',
      variant = 'info',
      duration = DEFAULT_DURATION,
    } = options;

    const id = `toast-${toastCounter += 1}`;
    const nextToast = {
      id,
      title,
      message,
      variant,
    };

    setToasts((currentToasts) => [...currentToasts, nextToast]);

    if (duration > 0 && typeof window !== 'undefined') {
      const timeoutId = window.setTimeout(() => {
        dismissToast(id);
      }, duration);

      timeoutMapRef.current.set(id, timeoutId);
    }

    return id;
  }, [dismissToast]);

  useEffect(() => () => {
    timeoutMapRef.current.forEach((timeoutId) => {
      window.clearTimeout(timeoutId);
    });
    timeoutMapRef.current.clear();
  }, []);

  return (
    <ToastContext.Provider value={{ showToast, dismissToast }}>
      {children}
      <div className="toast-viewport" aria-live="polite" aria-atomic="true">
        {toasts.map((toast) => {
          const meta = TOAST_META[toast.variant] || TOAST_META.info;

          return (
            <div
              key={toast.id}
              className={`toast-card toast-card--${toast.variant}`}
              role={toast.variant === 'error' ? 'alert' : 'status'}
            >
              <div className="toast-icon" aria-hidden="true">{meta.icon}</div>
              <div className="toast-content">
                {toast.title && <p className="toast-title">{toast.title}</p>}
                {toast.message && <p className="toast-message">{toast.message}</p>}
              </div>
              <button
                type="button"
                className="toast-dismiss"
                onClick={() => dismissToast(toast.id)}
                aria-label={`Dismiss ${meta.label.toLowerCase()} notification`}
              >
                x
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);

  if (!context) {
    throw new Error('useToast must be used within a ToastProvider.');
  }

  return context;
};
